export const queryKeys = {
  // Auth
  auth: {
    user: () => ['auth', 'user'] as const,
    session: () => ['auth', 'session'] as const,
  },

  // Customers
  customers: {
    all: () => ['customers'] as const,
    list: (filters?: any) => ['customers', 'list', filters] as const,
    detail: (id: string) => ['customers', 'detail', id] as const,
    search: (query: string) => ['customers', 'search', query] as const,
    stats: () => ['customers', 'stats'] as const,
    termsHistory: (id: string) => ['customers', 'termsHistory', id] as const,
    loginHistory: (id: string) => ['customers', 'loginHistory', id] as const,
    addressNeighbors: (id: string) => ['customers', 'addressNeighbors', id] as const,
    related: (id: string) => ['customers', 'related', id] as const,
    pendingAccessRequests: (id: string) => ['customers', 'pendingAccessRequests', id] as const,
  },

  // Crews
  crews: {
    all: () => ['crews'] as const,
    list: (filters?: any) => ['crews', 'list', filters] as const,
    detail: (id: string) => ['crews', 'detail', id] as const,
    members: (crewId: string) => ['crews', 'members', crewId] as const,
    memberTimeHistory: (memberId: string, days?: number) => ['crews', 'memberTimeHistory', memberId, days] as const,
  },

  // Organizations
  organizations: {
    all: () => ['organizations'] as const,
    current: () => ['organizations', 'current'] as const,
    members: (orgId?: string) => ['organizations', 'members', orgId] as const,
    detail: (id: string) => ['organizations', 'detail', id] as const,
    platformInvoices: () => ['organizations', 'platform-invoices'] as const,
  },

  // Jobs
  jobs: {
    all: () => ['jobs'] as const,
    list: (filters?: any) => ['jobs', 'list', filters] as const,
    detail: (id: string) => ['jobs', 'detail', id] as const,
    history: (customerId: string) => ['jobs', 'history', customerId] as const,
  },

  // Service Templates
  serviceTemplates: {
    all: () => ['serviceTemplates'] as const,
    list: (filters?: any) => ['serviceTemplates', 'list', filters] as const,
    detail: (id: string) => ['serviceTemplates', 'detail', id] as const,
  },

  // Service Bundles
  serviceBundles: {
    all: () => ['serviceBundles'] as const,
    list: (filters?: any) => ['serviceBundles', 'list', filters] as const,
    detail: (id: string) => ['serviceBundles', 'detail', id] as const,
    price: (id: string, params?: any) =>
      ['serviceBundles', 'price', id, params] as const,
  },

  // Payment Plans
  paymentPlans: {
    all: () => ['paymentPlans'] as const,
    customer: (customerId: string) => ['paymentPlans', 'customer', customerId] as const,
  },

  // Visits
  visits: {
    all: () => ['visits'] as const,
    list: (filters?: any) => ['visits', 'list', filters] as const,
    detail: (id: string) => ['visits', 'detail', id] as const,
  },

  // Office photo review wall (RUT-2455)
  photoReview: {
    all: () => ['photoReview'] as const,
    feed: (state: string) => ['photoReview', 'feed', state] as const,
  },

  // Subscriptions
  subscriptions: {
    all: () => ['subscriptions'] as const,
    list: (filters?: any) => ['subscriptions', 'list', filters] as const,
    detail: (id: string) => ['subscriptions', 'detail', id] as const,
    customer: (customerId: string) => ['subscriptions', 'customer', customerId] as const,
  },

  // Subscription Stats
  subscriptionStats: (serviceTemplateId?: string) =>
    serviceTemplateId
      ? ['subscriptionStats', serviceTemplateId] as const
      : ['subscriptionStats'] as const,

  // Payments
  payments: {
    all: () => ['payments'] as const,
    list: (filters?: any) => ['payments', 'list', filters] as const,
    detail: (id: string) => ['payments', 'detail', id] as const,
    customer: (customerId: string) => ['payments', 'customer', customerId] as const,
  },

  // Location Tracking
  locationTracking: {
    all: () => ['locationTracking'] as const,
    visit: (visitId: string) => ['locationTracking', 'visit', visitId] as const,
    crew: (crewId: string) => ['locationTracking', 'crew', crewId] as const,
  },

  // Rate Requests
  rateRequests: {
    all: () => ['rateRequests'] as const,
    list: (filters?: any) => ['rateRequests', 'list', filters] as const,
    detail: (id: string) => ['rateRequests', 'detail', id] as const,
  },

  // Service Areas
  serviceAreas: {
    all: () => ['serviceAreas'] as const,
    list: (filters?: any) => ['serviceAreas', 'list', filters] as const,
    detail: (id: string) => ['serviceAreas', 'detail', id] as const,
  },

  // Parcel Measurements
  parcelMeasurements: {
    all: () => ['parcelMeasurements'] as const,
    byCustomer: (customerId: string) => ['parcelMeasurements', 'customer', customerId] as const,
    detail: (customerId: string, measurementId: string) => ['parcelMeasurements', 'detail', customerId, measurementId] as const,
  },

  // Customer Imagery
  customerImagery: {
    byCustomer: (customerId: string) => ['customerImagery', customerId] as const,
  },

  // User Profile
  users: {
    profile: () => ['users', 'profile'] as const,
    notificationPreferences: () => ['users', 'notificationPreferences'] as const,
  },

  // Dispatch Jobs
  dispatchJobs: {
    all: () => ['dispatchJobs'] as const,
    list: (filters?: any) => ['dispatchJobs', 'list', filters] as const,
    active: () => ['dispatchJobs', 'active'] as const,
    detail: (id: string) => ['dispatchJobs', 'detail', id] as const,
    visits: (jobId: string) => ['dispatchJobs', 'visits', jobId] as const,
  },

  // Roles
  roles: {
    all: () => ['roles'] as const,
    list: () => ['roles', 'list'] as const,
    orgList: () => ['roles', 'org-list'] as const,
  },

  // Picklist Items
  picklistItems: {
    all: () => ['picklistItems'] as const,
    byCategory: (category: string) => ['picklistItems', category] as const,
  },

  // Notification Templates
  notificationTemplates: {
    all: () => ['notificationTemplates'] as const,
  },

  // Message Templates
  messageTemplates: {
    all: () => ['messageTemplates'] as const,
    list: () => ['messageTemplates', 'list'] as const,
  },

  // Subcontractor Companies
  subcontractorCompanies: {
    all: () => ['subcontractorCompanies'] as const,
    list: () => ['subcontractorCompanies', 'list'] as const,
    visits: (companyId: string, from: string, to: string) =>
      ['subcontractorCompanies', 'visits', companyId, from, to] as const,
  },

  // Revenue Reports
  revenue: {
    all: () => ['revenue'] as const,
    summary: () => ['revenue', 'summary'] as const,
    serviceTemplate: () => ['revenue', 'serviceTemplate'] as const,
    periodVisits: (from: string, to: string, statuses: string) =>
      ['revenue', 'periodVisits', from, to, statuses] as const,
    taxRowVisits: (from: string, to: string, rowKey: string) =>
      ['revenue', 'taxRowVisits', from, to, rowKey] as const,
  },

  // Analytics
  analytics: {
    all: () => ['analytics'] as const,
    dailyPerformance: (startDate: string, endDate: string) =>
      ['analytics', 'dailyPerformance', startDate, endDate] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;